import { AutoCompleteModel } from 'models/AutoCompleteModel';
import styles from './styles.scss';
import { hasElements, rm } from 'utils';
import { AutoCompleteSuggestion } from 'models/AutoCompleteSuggestion';
import cn from 'classnames';
import connect from 'components/Shared/connect';
import { isMobile } from 'components/Shared/responsive';
import { SuggestedCategory } from './suggestedCategory';
import { SuggestedPhrase } from './suggestedPhrase';
import { SuggestedProduct } from './suggestedProduct';
import { SearchResultBlock } from './searchResultBlock';
import { SearchListItem } from './searchList';
import { SuggestedCategory as SuggestedCategoryModel } from 'models/SuggestedCategory';
import { SuggestedPhrase as SuggestedPhraseModel } from 'models/SuggestedPhrase';
import { SuggestedProduct as SuggestedProductModel } from 'models/SuggestedProduct';

const getResultLayout = ({
  suggestedCategories,
  suggestedBrandCategories,
  suggestedPhrases,
  suggestedProducts,
  suggestedDidYouMean,
  isMobile
}: Pick<
  AutoCompleteSuggestion,
  | 'suggestedBrandCategories'
  | 'suggestedCategories'
  | 'suggestedProducts'
  | 'suggestedDidYouMean'
  | 'suggestedPhrases'
> & { isMobile: boolean }) => {
  const hasCategories = hasElements(suggestedCategories);
  const hasBrands = hasElements(suggestedBrandCategories);
  const hasPhrases = hasElements(suggestedPhrases);
  const hasProducts = hasElements(suggestedProducts);
  const hasSuggestions = hasElements(suggestedDidYouMean);
  const hasLeftColumn = hasCategories || hasBrands || hasPhrases;
  const hasRightColumn = hasProducts;
  const singleColumn =
    (hasLeftColumn && !hasRightColumn) || (!hasLeftColumn && hasRightColumn) || isMobile;
  const shouldShowSuggestions = !hasLeftColumn && !hasRightColumn;

  return {
    hasLeftColumn,
    hasRightColumn,
    singleColumn: singleColumn || shouldShowSuggestions,
    hasCategories,
    hasBrands,
    hasPhrases,
    hasSuggestions,
    hasProducts
  };
};

type OwnProps = Pick<AutoCompleteModel, 'suggestion'> & {
  executeSearch: (url: string) => void;
  searchPhrase: string;
  reset: () => void;
  showResultView: boolean;
};

type MergedProps = OwnProps & { isMobile: boolean; searchUrl: string };

interface SearchResultProps extends AutoCompleteSuggestion {
  isMobile: boolean;
  searchUrl: string;
  searchPhrase: string;
  reset: () => void;
  executeSearch: (url: string) => void;
}

const SearchResult = ({
  suggestedCategories,
  suggestedBrandCategories,
  suggestedProducts,
  suggestedPhrases,
  suggestedDidYouMean,
  searchUrl,
  isMobile,
  searchPhrase,
  reset,
  executeSearch
}: SearchResultProps) => {
  const {
    hasBrands,
    hasCategories,
    hasPhrases,
    hasProducts,
    hasLeftColumn,
    hasRightColumn,
    singleColumn
  } = getResultLayout({
    suggestedCategories,
    suggestedBrandCategories,
    suggestedDidYouMean,
    suggestedPhrases,
    suggestedProducts,
    isMobile
  });

  return (
    <div className={cn(styles.dropdown, { [styles.single]: singleColumn })}>
      {hasLeftColumn && (
        <div className={styles.column}>
          <SearchResultBlock
            show={hasCategories}
            isMobile={isMobile}
            searchItems={suggestedCategories}
            heading={rm('search_new_categories')}
          >
            {(item: SuggestedCategoryModel) => (
              <SuggestedCategory reset={reset} key={item.id} {...item} />
            )}
          </SearchResultBlock>
          <SearchResultBlock
            show={hasBrands}
            isMobile={isMobile}
            searchItems={suggestedBrandCategories}
            heading={rm('general_brands')}
          >
            {(item: SuggestedCategoryModel) => (
              <SuggestedCategory
                showSubCategories={!isMobile}
                reset={reset}
                key={item.id}
                {...item}
                parentCategoryDisplayName={isMobile ? rm('search_new_brand') : undefined}
              />
            )}
          </SearchResultBlock>
          <SearchResultBlock
            show={hasPhrases}
            isMobile={isMobile}
            searchItems={suggestedPhrases}
            heading={rm('search_suggestions')}
          >
            {(item: SuggestedPhraseModel) => (
              <SuggestedPhrase
                executeSearch={executeSearch}
                key={item.id}
                searchUrl={searchUrl}
                reset={reset}
                {...item}
              />
            )}
          </SearchResultBlock>
        </div>
      )}
      {hasRightColumn && (
        <div className={styles.column}>
          <SearchResultBlock
            show={hasProducts}
            isMobile={isMobile}
            searchItems={suggestedProducts}
            heading={rm('search_new_products')}
          >
            {(item: SuggestedProductModel) => (
              <SuggestedProduct key={item.id} hidePicture={isMobile} reset={reset} {...item} />
            )}
          </SearchResultBlock>
        </div>
      )}
      {!hasLeftColumn && !hasRightColumn && (
        <div className={styles.column}>
          <SearchResultBlock
            show
            searchItems={suggestedDidYouMean}
            heading={`${rm('search_new_nohits')} ${searchPhrase}`}
          >
            {(item: SuggestedPhraseModel) => (
              <SearchListItem
                key={item.id}
                reset={reset}
                executeSearch={executeSearch}
                ticket={item.ticket}
                url={`${searchUrl}?phrase=${item.phrase}`}
                selected={item.selected}
              >
                {`${rm('search_new_didyoumean')} ${item.phrase}`}
              </SearchListItem>
            )}
          </SearchResultBlock>
        </div>
      )}
    </div>
  );
};

const SearchStart = ({
  latestSearches,
  popularSearchPhrases,
  searchUrl,
  reset,
  executeSearch
}: Pick<AutoCompleteSuggestion, 'latestSearches' | 'popularSearchPhrases'> &
  Pick<MergedProps, 'searchUrl' | 'reset' | 'executeSearch'>) => {
  return (
    <div className={cn(styles.dropdown, styles.start)}>
      <SearchResultBlock
        className={styles.alwaysBorders}
        show={hasElements(latestSearches)}
        searchItems={latestSearches || []}
        heading={rm('latest_searches')}
      >
        {(item: SuggestedPhraseModel) => (
          <SuggestedPhrase
            executeSearch={executeSearch}
            key={item.id}
            reset={reset}
            searchUrl={searchUrl}
            {...item}
          />
        )}
      </SearchResultBlock>
      <SearchResultBlock
        className={styles.alwaysBorders}
        show={hasElements(popularSearchPhrases)}
        searchItems={popularSearchPhrases || []}
        heading={rm('popular_searches')}
      >
        {(item: SuggestedPhraseModel) => (
          <SuggestedPhrase
            executeSearch={executeSearch}
            key={item.id}
            reset={reset}
            searchUrl={searchUrl}
            {...item}
          />
        )}
      </SearchResultBlock>
    </div>
  );
};

const _SearchDropdown = ({ suggestion, showResultView, ...props }: MergedProps) => {
  const _props = {
    ...props,
    ...suggestion
  };
  const content = showResultView ? <SearchResult {..._props} /> : <SearchStart {..._props} />;
  return content;
};

export const SearchDropdown = connect<MergedProps, OwnProps>(state => ({
  isMobile: isMobile(state),
  searchUrl: state.search.searchUrl
}))(_SearchDropdown);
